import PropTypes from 'prop-types';

import { PRODUCT_IDS, TOOL_IDS } from '@adsk/alloy-provider';

export const entityProps = {
  /** The entity ID */
  id: PropTypes.string.isRequired,
};

export const entityShape = PropTypes.shape(entityProps);

export const relationshipEntityShape = PropTypes.shape({
  /** The relationship ID */
  id: PropTypes.string.isRequired,
  /** The entities linked in this relationship */
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      domain: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
  ),
});

export const relationshipShape = PropTypes.shape({
  /** The entity ID in this relationship */
  entityId: PropTypes.string.isRequired,
  /** The entity domain in this relationship */
  domain: PropTypes.string.isRequired,
  /** The entity type in this relationship */
  type: PropTypes.string.isRequired,
  /** The relationship entity */
  relationship: relationshipEntityShape.isRequired,
});

export const entityCacheItemShape = PropTypes.shape({
  /** The entity */
  entity: entityShape,
  /** Whether the entity is currently loading */
  loading: PropTypes.bool,
  /** Whether fetching the entity raised an error */
  error: PropTypes.object,
  /** A callback to retry fetching the entity again */
  retry: PropTypes.func,
});

/**
 * Entity cache shape is an object of:
 * 'domain: {
 *   'type': {
 *     'id': {
 *       entity: {},
 *       loading: false,
 *       error: null,
 *       retry: () => {}
 *     }
 *   }
 * }
 */
export const entityCacheShape = PropTypes.objectOf(
  PropTypes.objectOf(PropTypes.objectOf(entityCacheItemShape)),
);

export const createFormPropsShape = PropTypes.shape({
  sourceMetadata: PropTypes.exact({
    locationId: PropTypes.string,
  }),
});

export const pluginShape = PropTypes.shape({
  /** The plugin metadata */
  metadata: PropTypes.shape({
    /** The type used for Relationship Service */
    type: PropTypes.string.isRequired,
    /** The domain used for Relationship Service */
    domain: PropTypes.string.isRequired,
  }),
  /** Render prop for icon */
  renderIcon: PropTypes.func,
  /** Optional product ID from project-entitlements, allowing users to add references only if exists */
  product: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** Optional tool ID from project-entitlements, allowing users to add references only if exists */
  toolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  /** Whether or not to show search box inside picker modal */
  showSearchBox: PropTypes.bool,
  /** Extra props to customize the picker modal */
  modalProps: PropTypes.object,
  /**
   * nested option if needed show second level in dropdown button
   */
  nestedOptions: PropTypes.shape({
    /** first level label  */
    label: PropTypes.node.isRequired,
    /** first level icon */
    renderIcon: PropTypes.func.isRequired,
    /** first level key */
    key: PropTypes.string.isRequired,
    /** category key in second level ,if you don't want to group second level with category,you can use any key in this*/
    nestedCategoryKey: PropTypes.string.isRequired,
  }),
  /** A component rendering translated plural type text */
  PluralText: PropTypes.func.isRequired,
  /** A component rendering translated modal picker title text */
  PickerModalTitle: PropTypes.func,
  /** A component rendering add relationships button text */
  AddRelationshipsButtonText: PropTypes.func,
  /** A component rendering selected entities count */
  SelectedCount: PropTypes.func,
  /** A component rendering create new button */
  CreateNewButtonText: PropTypes.func,
  /** Render prop to display create new button icon */
  renderCreateNewButtonIcon: PropTypes.func,
  /** A method for querying entities by text */
  queryEntities: PropTypes.func,
  /** A method for querying entities by an array of IDs */
  getEntitiesByIds: PropTypes.func.isRequired,
  /** A method for checking whether the user can create new entity */
  canCreate: PropTypes.func,
  /** Entity block component */
  EntityBlock: PropTypes.func.isRequired,
  /** Entity Cell Tooltip Item */
  EntityMenuItem: PropTypes.func,
  /** Entity picker item component */
  EntityPickerItem: PropTypes.func,
  /** Create new entity panel component */
  CreateNewEntityPanel: PropTypes.func,
  /** Filters button component */
  CustomFilterButton: PropTypes.func,
  /** Filters panel component */
  FiltersPanel: PropTypes.func,
  /** Custom data view */
  CustomDataView: PropTypes.func,
  /** Custom empty loader for picker modal */
  CustomPickerEmptyLoader: PropTypes.func,
  /** Custom empty state for picker modal */
  CustomPickerEmptyState: PropTypes.func,
  /** Custom no search results for picker modal */
  CustomPickerNoSearchResults: PropTypes.func,
  /** Custom error state for picker modal */
  CustomPickerError: PropTypes.func,
  /** Custom picker load more button */
  CustomPickerLoadMoreButton: PropTypes.func,
  /** Custom picker load more loader */
  CustomPickerLoadMoreLoader: PropTypes.func,
  /** Custom picker load more error */
  CustomPickerLoadMoreError: PropTypes.func,
  /** Custom block loader */
  CustomBlockEmptyLoader: PropTypes.func,
  /** Custom block error */
  CustomBlockError: PropTypes.func,
  /** Custom missing entities view */
  CustomMissingEntities: PropTypes.func,
  /** Custom remove entity modal content */
  CustomRemoveEntityModalContent: PropTypes.func,
  /** Callback for relationship addition */
  onAddRelationships: PropTypes.func,
  /** Callback for relationship removal */
  onRemove: PropTypes.func,
});
